import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import UploadVideoModal from '../../components/admin/UploadVideoModal';
import EditVideoModal from '../../components/admin/EditVideoModal';
import CreateModuleModal from '../../components/admin/CreateModuleModal';
import SectionModal from '../../components/admin/SectionModal';
import { Plus, Edit, Trash2, Eye, EyeOff, ChevronDown, ChevronRight, FolderOpen } from 'lucide-react';
import { motion } from 'framer-motion';

// Componente de Card de Vídeo
function VideoCard({ video, onEdit, onDelete, onTogglePublish }) {
  return (
    <div className="group bg-background-700 border-2 border-gray-700 hover:border-primary-500/50 rounded-xl p-4 transition-all duration-300">
      <div className="flex items-center gap-4">
        {/* Número da ordem */}
        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center text-white font-bold shadow-lg shadow-primary-500/30">
          {video.order_index + 1}
        </div>

        {/* Informações do vídeo */}
        <div className="flex-1 min-w-0">
          <h4 className="text-base font-bold text-text-primary group-hover:text-primary-500 transition-colors">
            {video.title_pt}
          </h4>
          <p className="text-xs text-text-secondary line-clamp-1">
            {video.description_pt || 'Sem descrição'}
          </p>
          {video.duration_seconds && (
            <p className="text-xs text-gray-500 mt-0.5">
              {Math.floor(video.duration_seconds / 60)}:{String(video.duration_seconds % 60).padStart(2, '0')} min
            </p>
          )}
        </div>

        {/* Ações */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => onTogglePublish(video)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
              video.is_published
                ? 'bg-success/20 text-success hover:bg-success/30'
                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {video.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
            {video.is_published ? 'Publicado' : 'Rascunho'}
          </button>
          <button 
            onClick={() => onEdit(video)}
            className="p-2 rounded-lg bg-primary-500/10 text-primary-500 hover:bg-primary-500/20 transition-all"
          >
            <Edit size={16} />
          </button>
          <button 
            onClick={() => onDelete(video.id, video.title_pt)}
            className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminModuleDetailPage() {
  const { id } = useParams();
  const [module, setModule] = useState(null);
  const [videos, setVideos] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModuleModalOpen, setIsEditModuleModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [isSectionModalOpen, setIsSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [expandedSections, setExpandedSections] = useState(new Set());

  const loadModuleData = useCallback(async () => {
    console.log('🔄 Carregando dados do módulo:', id);
    try {
      const [moduleRes, videosRes, sectionsRes] = await Promise.all([
        api.get(`/modules/${id}`),
        api.get(`/videos/module/${id}`),
        api.get(`/sections/module/${id}`)
      ]);
      
      console.log('📦 Módulo:', moduleRes.data);
      console.log('🎬 Vídeos:', videosRes.data);
      console.log('📁 Seções:', sectionsRes.data);
      
      setModule(moduleRes.data);
      setVideos(videosRes.data);
      setSections(sectionsRes.data);
      
      // Expandir todas as seções por padrão
      setExpandedSections(new Set(sectionsRes.data.map(s => s.id)));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handleUploadSuccess = (newVideo) => {
    console.log('✅ Upload bem-sucedido:', newVideo);
    setIsUploadModalOpen(false);
    loadModuleData();
  };

  const handleTogglePublish = async (video) => {
    // Atualização otimista
    setVideos(videos.map(v => 
      v.id === video.id ? { ...v, is_published: !v.is_published } : v
    ));

    try {
      console.log('🔄 Alterando status de publicação:', video.id);
      await api.put(`/videos/${video.id}`, {
        ...video,
        is_published: !video.is_published
      });
      console.log('✅ Status alterado');
    } catch (error) {
      console.error('❌ Erro ao alterar status:', error);
      // Reverter em caso de erro
      setVideos(videos.map(v => 
        v.id === video.id ? { ...v, is_published: video.is_published } : v
      ));
      alert('Erro ao alterar status de publicação');
    }
  };

  const handleDeleteVideo = async (videoId, videoTitle) => {
    if (!window.confirm(`Deseja realmente excluir o vídeo "${videoTitle}"?\n\nEsta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      console.log('🗑️ Excluindo vídeo:', videoId);
      await api.delete(`/videos/${videoId}`);
      console.log('✅ Vídeo excluído com sucesso');
      
      loadModuleData();
      
      alert('Vídeo excluído com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao excluir vídeo:', error);
      alert(error.response?.data?.error || 'Erro ao excluir vídeo');
    }
  };

  const handleDeleteSection = async (sectionId, sectionTitle) => {
    if (!window.confirm(`Deseja realmente excluir a seção "${sectionTitle}"?\n\nOs vídeos desta seção não serão excluídos, apenas ficarão sem seção.`)) {
      return;
    }

    try {
      await api.delete(`/sections/${sectionId}`);
      loadModuleData();
      alert('Seção excluída com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao excluir seção:', error);
      alert('Erro ao excluir seção');
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  useEffect(() => {
    loadModuleData();
  }, [loadModuleData]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="text-center py-16 bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl">
        <svg className="w-20 h-20 mx-auto text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-red-400 text-lg font-semibold">Módulo não encontrado</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Header do Módulo */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl p-8 mb-12"
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-4xl font-bold text-text-primary mb-4">
              {module.title_pt}
            </h1>
            <p className="text-text-secondary text-lg">
              {module.description_pt}
            </p>
          </div>
          <button
            onClick={() => setIsEditModuleModalOpen(true)}
            className="p-3 rounded-lg bg-primary-500/10 text-primary-500 hover:bg-primary-500/20 transition-colors"
          >
            <Edit size={20} />
          </button>
        </div>
      </motion.div>

      {/* Header de Vídeos */}
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-text-primary">
          Conteúdo do Módulo
        </h2>
        <div className="flex gap-3">
          <button 
            onClick={() => setIsSectionModalOpen(true)}
            className="bg-background-700 hover:bg-background-600 text-white px-6 py-3 rounded-2xl font-semibold transition-all flex items-center gap-2"
          >
            <FolderOpen size={20} />
            Nova Seção
          </button>
          <button 
            onClick={() => setIsUploadModalOpen(true)}
            className="bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-2xl font-semibold shadow-lg shadow-primary-500/20 transition-all duration-300 hover:scale-105 flex items-center gap-2"
          >
            <Plus size={20} />
            Upload de Vídeo
          </button>
        </div>
      </div>

      <UploadVideoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        moduleId={id}
        sections={sections}
        onSuccess={handleUploadSuccess}
      />

      {/* Modal de Edição de Módulo */}
      <CreateModuleModal
        isOpen={isEditModuleModalOpen}
        onClose={() => setIsEditModuleModalOpen(false)}
        onSuccess={(updatedModule) => {
          setModule(updatedModule);
          setIsEditModuleModalOpen(false);
        }}
        editMode={true}
        moduleData={module}
      />

      {/* Modal de Edição de Vídeo */}
      <EditVideoModal
        isOpen={!!editingVideo}
        onClose={() => setEditingVideo(null)}
        video={editingVideo}
        sections={sections}
        onSuccess={() => {
          console.log('🔄 Recarregando dados após edição...');
          loadModuleData();
          setEditingVideo(null);
        }}
      />

      {/* Modal de Seção */}
      <SectionModal
        isOpen={isSectionModalOpen}
        onClose={() => {
          setIsSectionModalOpen(false);
          setEditingSection(null);
        }}
        moduleId={id}
        section={editingSection}
        onSuccess={() => {
          loadModuleData();
          setIsSectionModalOpen(false);
          setEditingSection(null);
        }}
      />

      {videos.length === 0 && sections.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-16 bg-background-800 dark:bg-background-800 border border-gray-700 dark:border-gray-700 rounded-3xl"
        >
          <svg className="w-20 h-20 mx-auto text-gray-600 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
          <p className="text-text-primary text-lg font-medium mb-2">
            Nenhum vídeo adicionado ainda
          </p>
          <p className="text-text-secondary text-sm">
            Clique no botão acima para fazer upload do primeiro vídeo
          </p>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {/* Seções */}
          {sections.sort((a, b) => a.order_index - b.order_index).map((section) => {
            const sectionVideos = videos
              .filter(v => v.section_id === section.id)
              .sort((a, b) => a.order_index - b.order_index);
            const isExpanded = expandedSections.has(section.id);

            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-background-800 border-2 border-gray-700 rounded-2xl overflow-hidden"
              >
                {/* Section Header */}
                <div className="flex items-center justify-between p-6 bg-background-700/50">
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="flex items-center gap-4 flex-1 text-left"
                  >
                    {isExpanded ? (
                      <ChevronDown size={24} className="text-primary-500" />
                    ) : (
                      <ChevronRight size={24} className="text-gray-500" />
                    )}
                    <FolderOpen size={24} className="text-primary-500" />
                    <div>
                      <h3 className="text-xl font-bold text-text-primary">
                        {section.title_pt}
                      </h3>
                      {section.description_pt && (
                        <p className="text-sm text-gray-400 mt-1">
                          {section.description_pt}
                        </p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {sectionVideos.length} {sectionVideos.length === 1 ? 'vídeo' : 'vídeos'}
                      </p>
                    </div>
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingSection(section);
                        setIsSectionModalOpen(true);
                      }}
                      className="p-2.5 rounded-xl bg-primary-500/10 text-primary-500 hover:bg-primary-500/20 transition-all"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeleteSection(section.id, section.title_pt)}
                      className="p-2.5 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Section Videos */}
                {isExpanded && sectionVideos.length > 0 && (
                  <div className="p-4 space-y-3">
                    {sectionVideos.map((video) => (
                      <VideoCard
                        key={video.id}
                        video={video}
                        onEdit={setEditingVideo}
                        onDelete={handleDeleteVideo}
                        onTogglePublish={handleTogglePublish}
                      />
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}

          {/* Vídeos sem seção */}
          {videos.filter(v => !v.section_id).length > 0 && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-400 flex items-center gap-2">
                <span>Vídeos sem seção</span>
                <span className="text-sm text-gray-600">
                  ({videos.filter(v => !v.section_id).length})
                </span>
              </h3>
              {videos
                .filter(v => !v.section_id)
                .sort((a, b) => a.order_index - b.order_index)
                .map((video) => (
                  <VideoCard
                    key={video.id}
                    video={video}
                    onEdit={setEditingVideo}
                    onDelete={handleDeleteVideo}
                    onTogglePublish={handleTogglePublish}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
